import { inject, Injectable, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import * as CryptoJS from 'crypto-js';
import { APP_CONSTANTS } from '@constants';
import { Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { ToasterService } from '@shared/services/toaster.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private router = inject(Router);
  private encryptionKey = 'zsgj8o4ugor4c47red9kcwd0tikq237efcqumg4b4totipxhab';

  // Add signals to track auth state
  private _isAuthenticated = signal(false);
  private _currentUser = signal<any>(null);

  // Public readonly signals
  public readonly isAuthenticated$ = this._isAuthenticated.asReadonly();
  public readonly currentUser$ = this._currentUser.asReadonly();

  constructor(
    private cookieService: CookieService,
    private toaster: ToasterService
  ) {}

  /**
   * Initialize authentication state from cookies
   * This should be called on app startup
   */
  initializeAuth(): void {
    const token = this.getCookie(APP_CONSTANTS.COOKIE_NAMES.AUTH_TOKEN);
    const userData = this.getCookie(APP_CONSTANTS.COOKIE_NAMES.USER);

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        this._isAuthenticated.set(true);
        this._currentUser.set(user);
      } catch (error) {
        // If user data is corrupted, clear cookies
        this.clearAuthCookies();
      }
    } else {
      this._isAuthenticated.set(false);
      this._currentUser.set(null);
    }
  }

  /**
   * Set authentication state when user logs in
   */
  setAuthenticatedUser(user: any, token: string): void {
    this.setCookie(APP_CONSTANTS.COOKIE_NAMES.AUTH_TOKEN, token);
    this.setCookie(APP_CONSTANTS.COOKIE_NAMES.USER, JSON.stringify(user));
    this._isAuthenticated.set(true);
    this._currentUser.set(user);
  }

  /**
   * Clear authentication cookies
   */
  private clearAuthCookies(): void {
    this.deleteCookie(APP_CONSTANTS.COOKIE_NAMES.AUTH_TOKEN);
    this.deleteCookie(APP_CONSTANTS.COOKIE_NAMES.USER);
    this._isAuthenticated.set(false);
    this._currentUser.set(null);
  }

  /**
   * Validates the cookie name.
   * @param name - Cookie name
   * @returns `true` if valid, otherwise `false`
   */
  private isValidInput(name: string): boolean {
    if (!name || typeof name !== 'string' || name.trim() === '') {
      this.toaster.show('Cookie name must be a non-empty string.', 'error');
      return false;
    }
    return true;
  }

  /**
   * Encrypts a string using AES.
   * @param value - Plain text
   * @returns Encrypted text
   */
  private encrypt(value: string): string {
    try {
      return CryptoJS.AES.encrypt(value, this.encryptionKey).toString();
    } catch {
      return '';
    }
  }

  /**
   * Decrypts an AES-encrypted string.
   * @param value - Encrypted text
   * @returns Plain text
   */
  private decrypt(value: string): string {
    try {
      if (!value || value.trim() === '') return '';
      const bytes = CryptoJS.AES.decrypt(value, this.encryptionKey);
      return bytes.toString(CryptoJS.enc.Utf8) || '';
    } catch {
      return '';
    }
  }

  /**
   * Sets a cookie with encrypted value.
   * @param name - Cookie name
   * @param value - Plain value
   * @param days - Expiry in days (default: 1)
   */
  setCookie(name: string, value: string, days: number = 1): void {
    if (!this.isValidInput(name)) return;

    try {
      const encryptedValue = this.encrypt(value);
      if (!encryptedValue) return;

      const path = '/';
      const secure = location.protocol === 'https:';
      const sameSite: 'Lax' = 'Lax';

      this.cookieService.set(
        name,
        encryptedValue,
        days,
        path,
        undefined,
        secure,
        sameSite
      );
    } catch {}
  }

  /**
   * Gets and decrypts a cookie value.
   * @param name - Cookie name
   * @returns Decrypted value
   */
  getCookie(name: string): string {
    if (!this.isValidInput(name)) return '';

    try {
      if (!this.cookieService.check(name)) return '';
      const encryptedValue = this.cookieService.get(name);
      if (!encryptedValue) return '';

      const decryptedValue = this.decrypt(encryptedValue);
      if (!decryptedValue && encryptedValue) {
        this.deleteCookie(name);
        return '';
      }

      return decryptedValue;
    } catch {
      return '';
    }
  }

  /**
   * Checks whether a cookie exists.
   * @param name - Cookie name
   * @returns `true` if exists
   */
  cookieExists(name: string): boolean {
    if (!this.isValidInput(name)) return false;
    return this.cookieService.check(name);
  }

  /**
   * Deletes a cookie.
   * @param name - Cookie name
   */
  deleteCookie(name: string): void {
    if (!this.isValidInput(name)) return;
    this.cookieService.delete(name, '/');
  }

  /**
   * Clears all cookies for the domain.
   */
  clearAllCookies(): void {
    const cookies = document.cookie.split(';');
    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      this.cookieService.delete(name, '/');
    });
  }

  /**
   * Verifies if the user is authenticated via token presence.
   * @returns `true` if authenticated
   */
  isAuthenticated(): boolean {
    // First check the signal state
    if (this._isAuthenticated()) {
      return true;
    }

    // If signal is false, check cookies as fallback
    const token = this.getCookie(APP_CONSTANTS.COOKIE_NAMES.AUTH_TOKEN);
    const hasToken = !!token && token.trim() !== '';

    // Update signal if we found a token
    if (hasToken && !this._isAuthenticated()) {
      this.initializeAuth();
    }

    return hasToken;
  }

  /**
   * Retrieves user data from cookie (parsed JSON).
   * @returns User object or `null`
   */
  getUserData(): any {
    // First check signal state
    if (this._currentUser()) {
      return this._currentUser();
    }

    // Fallback to cookies
    const userData = this.getCookie(APP_CONSTANTS.COOKIE_NAMES.USER);
    if (!userData) return null;

    try {
      const user = JSON.parse(userData);
      this._currentUser.set(user);
      return user;
    } catch {
      this.deleteCookie(APP_CONSTANTS.COOKIE_NAMES.USER);
      return null;
    }
  }

  /**
   * Logs out the user by clearing cookies and storage.
   * @returns An empty observable
   */
  handleLogout() {
    this.clearAuthCookies();
    this.clearRememberedCredentials();
    localStorage.clear();
    sessionStorage.clear();
    this.toaster.show('You have successfully logged out.', 'success');
    this.router.navigate(['/login']);
    return EMPTY;
  }

  /**
   * Stores login credentials securely in localStorage.
   * @param email - User email
   * @param password - User password
   */
  rememberCredentials(email: string, password: string): void {
    try {
      const encryptedEmail = this.encrypt(email);
      const encryptedPassword = this.encrypt(password);
      localStorage.setItem('rememberedEmail', encryptedEmail);
      localStorage.setItem('rememberedPassword', encryptedPassword);
    } catch {}
  }

  /**
   * Retrieves remembered login credentials from localStorage.
   * @returns Email & password or `null`
   */
  getRememberedCredentials(): { email: string; password: string } | null {
    try {
      const encryptedEmail = localStorage.getItem('rememberedEmail');
      const encryptedPassword = localStorage.getItem('rememberedPassword');

      if (encryptedEmail && encryptedPassword) {
        const email = this.decrypt(encryptedEmail);
        const password = this.decrypt(encryptedPassword);
        return { email, password };
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Clears remembered credentials from localStorage.
   */
  clearRememberedCredentials(): void {
    localStorage.removeItem('rememberedEmail');
    localStorage.removeItem('rememberedPassword');
  }

  /**
   * Encrypts a plain router param value (like ID or token) for safe transmission.
   * @param value - Plain router param
   * @returns Encrypted and URI-safe param
   */
  encryptRouteParam(value: string): string {
    if (!value || typeof value !== 'string') return '';
    try {
      const encrypted = this.encrypt(value);
      return encodeURIComponent(encrypted); // Make it URL-safe
    } catch {
      return '';
    }
  }

  /**
   * Decrypts an encrypted router param back to its original value.
   * @param param - Encrypted router param
   * @returns Decrypted plain value
   */
  decryptRouteParam(param: string): string {
    if (!param || typeof param !== 'string') return '';
    try {
      const decoded = decodeURIComponent(param);
      return this.decrypt(decoded);
    } catch {
      return '';
    }
  }

  /**
   * Updates only the user's name in cookie and signal.
   * Call this after profile update.
   */
  updateUserName(newName: string): void {
    const user = this.getUserData();
    if (user) {
      user.name = newName;

      // Update cookie and signal
      this.setCookie(APP_CONSTANTS.COOKIE_NAMES.USER, JSON.stringify(user));
      this._currentUser.set(user);
    }
  }
}
